import { useState, useRef, useEffect, useCallback } from "react";

interface QuestionCardProps {
  surveyTitle: string;
  questionText: string;
  questionNumber: number;
  totalQuestions: number;
  options: { id: string; option_text: string }[];
  rewardAmount: string | null;
  onAnswer: (optionId: string) => void;
  onSkip: () => void;
  isSubmitting: boolean;
}

export default function QuestionCard({
  surveyTitle,
  questionText,
  questionNumber,
  totalQuestions,
  options,
  rewardAmount,
  onAnswer,
  onSkip,
  isSubmitting,
}: QuestionCardProps) {
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [swipeX, setSwipeX] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const [exitDirection, setExitDirection] = useState<"left" | "right" | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const isHorizontalSwipe = useRef(false);

  const SWIPE_THRESHOLD = 120;

  // Reset state when question changes
  useEffect(() => {
    setSelectedOption(null);
    setSwipeX(0);
    setExitDirection(null);
    setIsSwiping(false);
  }, [questionText]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    isHorizontalSwipe.current = false;
    setIsSwiping(true);
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isSwiping) return;
    const deltaX = e.touches[0].clientX - touchStartX.current;
    const deltaY = e.touches[0].clientY - touchStartY.current;

    // Determine swipe direction on first significant movement
    if (!isHorizontalSwipe.current && Math.abs(deltaX) > 10) {
      isHorizontalSwipe.current = Math.abs(deltaX) > Math.abs(deltaY);
    }

    if (isHorizontalSwipe.current) {
      e.preventDefault();
      // Only allow left swipe (skip)
      setSwipeX(Math.min(0, deltaX));
    }
  }, [isSwiping]);

  const handleTouchEnd = useCallback(() => {
    setIsSwiping(false);
    if (swipeX < -SWIPE_THRESHOLD) {
      setExitDirection("left");
      setTimeout(() => onSkip(), 300);
    } else {
      setSwipeX(0);
    }
  }, [swipeX, onSkip]);

  const handleSkipClick = () => {
    setExitDirection("left");
    setTimeout(() => onSkip(), 300);
  };

  const handleSubmit = () => {
    if (!selectedOption) return;
    setExitDirection("right");
    setTimeout(() => onAnswer(selectedOption), 300);
  };

  const opacity = exitDirection ? 0 : 1 - Math.abs(swipeX) / 400;
  const rotation = swipeX / 30;
  const translateX = exitDirection === "left" ? -600 : exitDirection === "right" ? 600 : swipeX;

  return (
    <div className="relative w-full max-w-lg mx-auto" style={{ minHeight: "420px" }}>
      {/* Skip hint overlay */}
      {swipeX < -40 && !exitDirection && (
        <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
          <div
            className="bg-red-500/20 border-2 border-red-500 text-red-400 rounded-2xl px-6 py-3 text-xl font-bold uppercase tracking-wider"
            style={{ opacity: Math.min(1, Math.abs(swipeX) / SWIPE_THRESHOLD) }}
          >
            Skip →
          </div>
        </div>
      )}

      {/* Card */}
      <div
        ref={cardRef}
        className="card border-dark-border select-none"
        style={{
          transform: `translateX(${translateX}px) rotate(${rotation}deg)`,
          opacity,
          transition: exitDirection || !isSwiping ? "all 0.3s ease-out" : "none",
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="text-xs font-mono text-gray-500 bg-dark-800 px-2 py-1 rounded">
              Q{questionNumber}/{totalQuestions}
            </span>
            <span className="text-xs text-gray-500 truncate max-w-[180px]">
              {surveyTitle}
            </span>
          </div>
          {rewardAmount && (
            <span className="text-xs font-semibold text-green-400 bg-green-500/10 px-2 py-1 rounded">
              +{rewardAmount} coins
            </span>
          )}
        </div>

        {/* Question */}
        <h2 className="text-xl md:text-2xl font-bold text-gray-100 mb-6 leading-snug">
          {questionText}
        </h2>

        {/* Options */}
        <div className="space-y-3 mb-6">
          {options.map((option) => (
            <button
              key={option.id}
              onClick={() => setSelectedOption(option.id)}
              disabled={isSubmitting}
              className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                selectedOption === option.id
                  ? "border-primary-500 bg-primary-500/10 text-gray-100"
                  : "border-dark-border hover:border-dark-600 text-gray-300 hover:text-gray-100"
              }`}
            >
              <span className="text-sm md:text-base">{option.option_text}</span>
            </button>
          ))}
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={handleSkipClick}
            disabled={isSubmitting}
            className="btn btn-secondary flex-1 flex items-center justify-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
            </svg>
            Skip
          </button>
          <button
            onClick={handleSubmit}
            disabled={!selectedOption || isSubmitting}
            className={`btn flex-[2] flex items-center justify-center gap-2 ${
              selectedOption
                ? "btn-primary"
                : "bg-dark-700 text-gray-500 cursor-not-allowed border border-dark-border"
            }`}
          >
            {isSubmitting ? (
              "Submitting..."
            ) : (
              <>
                Submit Answer
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </>
            )}
          </button>
        </div>

        {/* Mobile swipe hint */}
        <p className="text-center text-xs text-gray-600 mt-4 md:hidden">
          ← Swipe left to skip
        </p>
      </div>
    </div>
  );
}
